import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from "styled-components";
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import Colours from '@shared/ui/atoms/Colours';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { useFloorPlanLayout } from '@shared/ui/organisms/floor-plan/useFloorPlanLayout';
import { FloorHeatMap } from '@features/reporting/services/OfficeHeatMapService';
import { binStyle, formatPercent, heatBin } from "./heatScale";
import { areaRatio, deskRatio, describe, roomRatio, tooltipContent } from "./floorHeat";
import { HeatTooltip, useHeatTooltip } from "./HeatTooltip";

// 1 is the whole plan fitted to the box; below that it shrinks further, kept centred
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;

interface Props {
  floor: FloorHeatMap;
}

// The hatching already says there is no capacity, so no dash after the name
function planLabel(name: string, ratio: number | undefined): string {
  return ratio === undefined ? name : `${name} · ${formatPercent(ratio)}`;
}

export function HeatMapFloorPlan({floor}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const {tip, markProps, hide} = useHeatTooltip();
  const {imageSrc, legacyLayout, desks, planWidth, planHeight, onImageLoad} = useFloorPlanLayout(floor.plan);

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      setContainerSize({w: containerRef.current.clientWidth, h: containerRef.current.clientHeight});
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const usageByDesk = useMemo(() => new Map(floor.desks.map(d => [d.deskId, d])), [floor.desks]);
  const areasById = useMemo(() => new Map(floor.areas.map(a => [a.areaId, a])), [floor.areas]);
  const areasByLabel = useMemo(() => new Map(floor.areas.map(a => [a.label, a])), [floor.areas]);
  const roomsById = useMemo(() => new Map(floor.meetingRooms.map(r => [r.roomId, r])), [floor.meetingRooms]);

  // Legacy layouts carry their own area positions without ids, so they are matched by label
  const placedAreas = useMemo(() => {
    if (legacyLayout?.areas) {
      return legacyLayout.areas.map((a, index) => ({key: `legacy-${index}`, label: a.label, x: a.x, y: a.y, width: a.width, height: a.height, heat: areasByLabel.get(a.label)}));
    }
    return floor.plan.areas.map(a => ({key: `area-${a.id}`, label: a.label, x: a.x, y: a.y, width: a.width ?? undefined, height: a.height ?? undefined, heat: areasById.get(a.id)}));
  }, [legacyLayout, floor.plan.areas, areasById, areasByLabel]);

  const scale = containerSize && planWidth && planHeight
    ? Math.min(containerSize.w / planWidth, containerSize.h / planHeight)
    : 1;

  return (
    <>
      <MapContainer ref={containerRef}>
        {/* Moving the plan would leave the tooltip pointing at the wrong place */}
        <TransformWrapper minScale={MIN_ZOOM} maxScale={MAX_ZOOM} initialScale={1} centerZoomedOut={true}
                          onTransformed={hide}>
          <TransformComponent wrapperStyle={{width: '100%', height: '100%'}}
                              contentStyle={{width: '100%', height: '100%'}}>
            <Canvas>
              {imageSrc && (
                <ScaledPlan style={{
                  width: planWidth ?? undefined,
                  height: planHeight ?? undefined,
                  transform: `translate(-50%, -50%) scale(${scale})`,
                }}>
                  <PlanImage src={imageSrc} alt="" onLoad={onImageLoad} />

                  {placedAreas.filter(a => a.width && a.height).map(area => {
                    const ratio = area.heat ? areaRatio(area.heat, floor.daysInRange) : undefined;
                    const bin = heatBin(ratio);
                    return (
                      <AreaRect key={area.key}
                                {...markProps(describe(area.label, ratio), tooltipContent(area.label, ratio))}
                                style={{left: Number(area.x), top: Number(area.y), width: Number(area.width), height: Number(area.height),
                                  borderColor: bin.edge ?? bin.fill, backgroundColor: `${bin.fill}55`, backgroundImage: bin.texture}}>
                        <PlanLabel>{planLabel(area.label, ratio)}</PlanLabel>
                      </AreaRect>
                    );
                  })}

                  {floor.plan.meetingRooms
                    // Same guard as the main floor plan and Waypoint screens, which treat 0 as unset
                    .filter(room => !!room.x && !!room.y && !!room.width && !!room.height)
                    .map(room => {
                      const heat = roomsById.get(room.id);
                      const ratio = heat ? roomRatio(heat) : undefined;
                      const bin = heatBin(ratio);
                      const detail = heat ? `${heat.totalHoursBooked.toFixed(1)} hours booked` : undefined;
                      return (
                        <RoomRect key={room.id}
                                  {...markProps(describe(room.name, ratio, detail), tooltipContent(room.name, ratio, detail))}
                                  style={{left: room.x, top: room.y, width: Number(room.width), height: Number(room.height),
                                    borderColor: bin.edge ?? bin.fill, backgroundColor: `${bin.fill}88`, backgroundImage: bin.texture}}>
                          <PlanLabel>{planLabel(room.name, ratio)}</PlanLabel>
                        </RoomRect>
                      );
                    })}

                  {desks.map(desk => {
                    const heat = usageByDesk.get(desk.deskId);
                    const ratio = heat ? deskRatio(heat, floor.daysInRange) : undefined;
                    const bin = heatBin(ratio);
                    const detail = heat ? `${heat.usage} of ${floor.daysInRange} days booked` : undefined;
                    return (
                      <DeskSpot key={desk.deskId}
                                {...markProps(describe(desk.label, ratio, detail), tooltipContent(desk.label, ratio, detail))}
                                style={{
                                  left: desk.x,
                                  top: desk.y,
                                  width: desk.width,
                                  height: desk.height,
                                  transform: `rotate(${desk.rotation}deg)`,
                                  ...binStyle(bin),
                                }}>
                        {formatPercent(ratio)}
                      </DeskSpot>
                    );
                  })}
                </ScaledPlan>
              )}
            </Canvas>
          </TransformComponent>
        </TransformWrapper>
      </MapContainer>
      <HeatTooltip tip={tip} />
    </>
  );
}

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 420px;
  overflow: hidden;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 8px;
  background: white;
  touch-action: none;
  @media (${tablet}) {
    height: 600px;
  }
`

const Canvas = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const ScaledPlan = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform-origin: center center;
`

const PlanImage = styled.img`
  display: block;
  user-select: none;
  pointer-events: none;
`

const AreaRect = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 2px dashed;
  border-radius: 8px;
  padding: 6px 10px;
`

const RoomRect = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 3px solid;
  border-radius: 16px;
  padding: 8px 12px;
  overflow: hidden;
`

const PlanLabel = styled.div`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.85);
  font-family: "soleil", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${Colours.black};
  white-space: nowrap;
`

const DeskSpot = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 2px solid white;
  border-radius: 6px;
  font-family: "soleil", sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: default;
  &:focus-visible {
    outline: 2px solid ${Colours.black};
  }
`
